import React, { useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, Menu } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { canAccessDashboard, filterMenus } from "@/lib/permissions";
import { MENUS } from "@/lib/menuKeys";
import { getMenuIcon } from "@/lib/menuIcons";
import LanguageSwitcher from "@/components/common/LanguageSwitcher";
import { useLocale } from "@/contexts/LocaleProvider";
import { useOnlineOrderInbox } from "@/hooks/useOnlineOrderInbox";
import { cn } from "@/lib/utils";

function CountBadge({ count, className }) {
  if (!count) return null;
  return (
    <span
      className={cn(
        "ms-1 inline-flex min-w-[18px] items-center justify-center rounded-full bg-rose-500 px-1.5 text-[10px] font-bold leading-4 text-white",
        className,
      )}
    >
      {count > 99 ? "99+" : count}
    </span>
  );
}

function isMenuActive(pathname, items) {
  return items.some((item) => pathname === item.to || pathname.startsWith(`${item.to}/`));
}

function itemBadge(item, counts) {
  if (item.to === "/online/orders") {
    return (counts.new || 0) + (counts.pending_delivery || 0);
  }
  return 0;
}

function itemPath(item, counts) {
  if (item.to === "/online/orders" && (counts.new || 0) + (counts.pending_delivery || 0) > 0) {
    return "/online/orders?attention=1";
  }
  return item.to;
}

function MobileNav({ visibleMenus, showDashboard, pathname, onNavigate, t, counts }) {
  return (
    <div className="flex flex-col gap-1 mt-2 px-2 pb-4">
      {showDashboard && (
        <Link
          to="/"
          onClick={onNavigate}
          className={cn(
            "flex items-center gap-2 px-3 py-2.5 rounded-xl text-[14px] font-medium mb-1 transition-colors",
            pathname === "/"
              ? "bg-stone-100 text-stone-900 font-semibold border border-stone-200"
              : "text-stone-800 hover:bg-stone-100/80",
          )}
        >
          {getMenuIcon("nav.dashboard")}
          {t("nav.dashboard")}
        </Link>
      )}
      {visibleMenus.map((menu) => {
        const isActive = isMenuActive(pathname, menu.items);
        const menuBadge = menu.items.reduce((sum, item) => sum + itemBadge(item, counts), 0);
        return (
          <Collapsible
            key={menu.labelKey}
            defaultOpen={isActive}
            className="border-b border-stone-100 last:border-b-0"
          >
            <CollapsibleTrigger
              className={cn(
                "flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-[14px] font-medium transition-colors hover:bg-stone-100/80",
                isActive ? "text-brand-primary-dark" : "text-stone-800",
                "[&[data-state=open]>svg]:rotate-180",
              )}
            >
              <span className="flex items-center gap-2">
                {getMenuIcon(menu.labelKey)}
                {t(menu.labelKey)}
                <CountBadge count={menuBadge} />
              </span>
              <ChevronDown size={16} className="text-slate-400 transition-transform duration-200" />
            </CollapsibleTrigger>
            <CollapsibleContent className="pb-1">
              <div className="flex flex-col pl-2">
                {menu.items.map((item) => {
                  const itemActive = pathname === item.to || pathname.startsWith(`${item.to}/`);
                  return (
                    <Link
                      key={item.to}
                      to={itemPath(item, counts)}
                      onClick={onNavigate}
                      className={cn(
                        "px-3 py-2 rounded-lg text-[13.5px] flex items-center gap-2 transition-colors",
                        itemActive
                          ? "bg-stone-100 text-brand-primary-dark font-medium"
                          : "text-stone-600 hover:bg-stone-100/80 hover:text-stone-900",
                      )}
                    >
                      {getMenuIcon(item.labelKey)}
                      {t(item.labelKey)}
                      <CountBadge count={itemBadge(item, counts)} />
                    </Link>
                  );
                })}
              </div>
            </CollapsibleContent>
          </Collapsible>
        );
      })}
    </div>
  );
}

export default function TopMenuBar() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const { isRtl } = useLocale();
  const permissions = user?.permissions || [];
  const [mobileOpen, setMobileOpen] = useState(false);
  const { counts } = useOnlineOrderInbox();

  const visibleMenus = useMemo(
    () => filterMenus(MENUS, permissions),
    [permissions],
  );

  const showDashboard = canAccessDashboard(permissions);

  if (!showDashboard && visibleMenus.length === 0) {
    return null;
  }

  return (
    <nav
      data-testid="top-menu-bar"
      className="h-11 flex items-center px-2 sm:px-3 bg-gradient-to-r from-brand-chrome via-brand-chrome-mid to-brand-chrome-dark text-white text-[12.5px] border-b border-brand-gold/25 shrink-0 gap-0.5 min-w-0 overflow-hidden shadow-brand-sm"
    >
      <div className="flex items-center gap-2 pe-2 sm:pe-3 me-1 border-e border-white/10 shrink-0">
        <div className="h-7 w-7 rounded-lg bg-gradient-to-br from-brand-primary to-brand-gold flex items-center justify-center text-white font-bold text-[12px] shadow-sm tracking-tight">
          L
        </div>
        <span className="hidden sm:inline font-semibold text-[12.5px] tracking-tight">
          {t("app.title")}
        </span>
      </div>

      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetTrigger asChild>
          <button
            type="button"
            className="lg:hidden relative h-8 w-8 inline-flex items-center justify-center rounded-lg hover:bg-white/10"
            aria-label="Open menu"
            data-testid="mobile-menu-btn"
          >
            <Menu size={18} />
            {(counts.new || 0) + (counts.pending_delivery || 0) > 0 && (
              <span className="absolute top-0.5 end-0.5 h-2 w-2 rounded-full bg-rose-400 ring-1 ring-brand-chrome-dark" />
            )}
          </button>
        </SheetTrigger>
        <SheetContent side={isRtl ? "right" : "left"} className="w-[min(100vw-2rem,320px)] p-0 overflow-y-auto bg-white">
          <SheetHeader className="px-4 py-3 border-b border-brand-surface-border text-left bg-gradient-to-r from-stone-50 to-brand-violet-soft/30">
            <SheetTitle className="text-[15px] flex items-center gap-2">
              <span className="h-6 w-6 rounded-md bg-gradient-to-br from-brand-primary to-brand-gold text-white text-[11px] font-bold flex items-center justify-center" aria-hidden>L</span> {t("app.title")}
            </SheetTitle>
          </SheetHeader>
          <div className="px-2 pb-6">
            <MobileNav
              visibleMenus={visibleMenus}
              showDashboard={showDashboard}
              pathname={pathname}
              onNavigate={() => setMobileOpen(false)}
              t={t}
              counts={counts}
            />
          </div>
        </SheetContent>
      </Sheet>

      <div className="hidden lg:flex flex-1 items-center gap-0.5 min-w-0 overflow-x-auto overflow-y-hidden [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
        {showDashboard && (
          <Link
            to="/"
            className={cn(
              "group relative px-2.5 h-8 inline-flex items-center gap-1.5 rounded-lg hover:bg-white/10 font-medium shrink-0 transition-colors",
              pathname === "/" && "bg-white/15",
            )}
          >
            {getMenuIcon("nav.dashboard")}
            {t("nav.dashboard")}
            <span
              className={cn(
                "absolute inset-x-2 bottom-0 h-0.5 rounded-full bg-brand-gold scale-x-0 transition-transform origin-left group-hover:scale-x-100",
                pathname === "/" && "scale-x-100",
              )}
            />
          </Link>
        )}

        {visibleMenus.map((menu) => {
          const menuBadge = menu.items.reduce((sum, item) => sum + itemBadge(item, counts), 0);
          return (
            <DropdownMenu key={menu.labelKey}>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className={cn(
                    "group relative px-2.5 h-8 inline-flex items-center gap-1 rounded-lg hover:bg-white/10 shrink-0 transition-colors",
                    isMenuActive(pathname, menu.items) && "bg-white/15",
                  )}
                >
                  {getMenuIcon(menu.labelKey)}
                  {t(menu.labelKey)}
                  <CountBadge count={menuBadge} className="bg-rose-400 text-white" />
                  <ChevronDown size={12} className="opacity-70" />
                  <span
                    className={cn(
                      "absolute inset-x-2 bottom-0 h-0.5 rounded-full bg-brand-gold scale-x-0 transition-transform origin-left group-hover:scale-x-100",
                      isMenuActive(pathname, menu.items) && "scale-x-100",
                    )}
                  />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="min-w-[220px] rounded-xl border-brand-surface-border shadow-brand-lift p-1.5"
              >
                {menu.items.map((item) => (
                  <DropdownMenuItem key={item.to} asChild>
                    <Link
                      to={itemPath(item, counts)}
                      className={cn(
                        "rounded-lg flex items-center gap-2 cursor-pointer",
                        pathname === item.to && "bg-stone-100 text-brand-primary-dark font-medium",
                      )}
                    >
                      {getMenuIcon(item.labelKey)}
                      <span className="flex-1">{t(item.labelKey)}</span>
                      <CountBadge count={itemBadge(item, counts)} className="bg-rose-500" />
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        })}
      </div>

      <div className="ms-auto pe-1 shrink-0">
        <LanguageSwitcher
          className="border-white/10 bg-white/10 text-white hover:bg-white/15 rounded-lg"
          compact
        />
      </div>

      {!showDashboard && visibleMenus.length === 1 && visibleMenus[0].items.length === 1 && (
        <Link
          to={visibleMenus[0].items[0].to}
          className="ms-1 px-2.5 h-8 inline-flex items-center gap-1.5 rounded-lg hover:bg-white/10 font-medium truncate"
        >
          {getMenuIcon(visibleMenus[0].items[0].labelKey)}
          {t(visibleMenus[0].items[0].labelKey)}
        </Link>
      )}
    </nav>
  );
}
